import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useEnsaiosAnalise } from "./useEnsaiosAnalise";
import { useEstabelecimento } from "./useEstabelecimento";
import { useFormulario } from "./useFormulario";
import { useLaboratorioAnalise } from "./useLaboratorioAnalise";
import { useMarcaProduto } from "./useMarcaProduto";
import { useMotivoNotificacao } from "./useMotivoNotificacao";
import { useNetworkStatus } from "./useNetworkStatus";
import { useProdutoAutorizado } from "./useProdutoAutorizado";
import { useProgramacaoColetaAnalise } from "./useProgramacaoColetaAnalise";
import { useServidor } from "./useServidor";
import { useUsuarioLogado } from "./useUsuarioLogado";

// HOOK QUE VAI SINCRONIZAR TODOS OS DADOS NECESSARIOS PARA USO DO APP AO FAZER O LOGIN
export const useSyncData = () => {
  const { user } = useAuth();
  const { isOnline } = useNetworkStatus();
  const {
    getEstabelecimentosApi,
    sincronizarEstabelecimentos,
    listaEstabelecimentos,
  } = useEstabelecimento();
  const { getEnsaiosApi, sincronizarEnsaios, listaEnsaios } =
    useEnsaiosAnalise();
  const { getServidoresApi, listaServidores, sincronizarServidores } =
    useServidor();
  const {
    getProdutosAutorizadosApi,
    listaProdutosAutorizados,
    sincronizarProdutosAutorizados,
  } = useProdutoAutorizado();
  const {
    getMarcasProdutosApi,
    listaMarcasProdutos,
    sincronizarMarcasProdutos,
  } = useMarcaProduto();
  const { getProgramacoesApi, listaProgramacoes, sincronizarProgramacoes } =
    useProgramacaoColetaAnalise();
  const {
    getMotivosNotificacaoApi,
    listaMotivosNotificacao,
    sincronizarMotivosNotificacao,
  } = useMotivoNotificacao();
  const { getUsuarioLogadoApi, sincronizarUsuarioLogado, usuarioLogado } =
    useUsuarioLogado();
  const { getFormulariosApi, listaFormularios, sincronizarFormularios } =
    useFormulario();
  const { getLaboratoriosApi, listaLaboratorios, sincronizarLaboratorios } =
    useLaboratorioAnalise();

  const { isSuccess: isSyncedUsuarioLogado, data: usuarioLogadoData } =
    useQuery({
      queryKey: ["ME"],
      enabled: !!user && isOnline,
      initialData: usuarioLogado,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      queryFn: async () => {
        const result = await getUsuarioLogadoApi();
        return result;
      },
    });

  //SINCRONIZAR AUTOMATICO OS ESTABELECIMENTOS
  const { isSuccess: isSyncedEstabelecimentos, data: estabelecimentosData } =
    useQuery({
      queryKey: ["ESTABELECIMENTOS"],
      enabled: !!user && isOnline,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      initialData: listaEstabelecimentos,
      queryFn: async () => {
        const result = await getEstabelecimentosApi();
        return result;
      },
    });

  //SINCRONIZAR AUTOMATICO OS ENSAIOS
  const { isSuccess: isSyncedEnsaios, data: ensaiosData } = useQuery({
    queryKey: ["ENSAIOS"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaEnsaios,
    queryFn: async () => {
      const result = await getEnsaiosApi();
      return result;
    },
  });

  //SINCRONIZAR AUTOMATICO OS SERVIDORES
  const { isSuccess: isSyncedServidores, data: servidoresData } = useQuery({
    queryKey: ["SERVIDORES"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaServidores,
    queryFn: async () => {
      const result = await getServidoresApi();
      return result;
    },
  });

  //SINCRONIZAR AUTOMATICO OS PRODUTOS AUTORIZADOS
  const {
    isSuccess: isSyncedProdutosAutorizados,
    data: produtosAutorizadosData,
  } = useQuery({
    queryKey: ["PRODUTOS-AUTORIZADOS"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaProdutosAutorizados,
    queryFn: async () => {
      const result = await getProdutosAutorizadosApi();
      return result;
    },
  });

  //SINCRONIZAR AUTOMATICO AS MARCAS DE PRODUTOS
  const { isSuccess: isSyncedMarcasProdutos, data: marcasProdutosData } =
    useQuery({
      queryKey: ["MARCAS-PRODUTOS"],
      enabled: !!user && isOnline,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      initialData: listaMarcasProdutos,
      queryFn: async () => {
        const result = await getMarcasProdutosApi();
        return result;
      },
    });

  //SINCRONIZAR AUTOMATICO AS PROGRAMACOES
  const { isSuccess: isSyncedProgramacoes, data: programacoesData } = useQuery({
    queryKey: ["PROGRAMACOES-COLETAS-ANALISES"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaProgramacoes,
    queryFn: async () => {
      const result = await getProgramacoesApi();
      return result;
    },
  });

  //SINCRONIZAR AUTOMATICO OS MOTIVOS DE NOTIFICACAO
  const {
    isSuccess: isSyncedMotivosNotificacao,
    data: motivosNotificacaoData,
  } = useQuery({
    queryKey: ["MOTIVOS-NOTIFICACAO"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaMotivosNotificacao,
    queryFn: async () => {
      const result = await getMotivosNotificacaoApi();
      return result;
    },
  });

  //SINCRONIZAR AUTOMATICO OS FORMULARIOS
  const { isSuccess: isSyncedFormularios, data: formulariosData } = useQuery({
    queryKey: ["FORMULARIOS"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaFormularios,
    queryFn: async () => {
      const result = await getFormulariosApi();
      return result;
    },
  });

  const { isSuccess: isSyncedLaboratorios, data: laboratoriosData } = useQuery({
    queryKey: ["LABORATORIOS-ANALISE"],
    enabled: !!user && isOnline,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    initialData: listaLaboratorios,
    queryFn: async () => {
      const result = await getLaboratoriosApi();
      return result;
    },
  });

  useEffect(() => {
    if (isSyncedUsuarioLogado && usuarioLogadoData) {
      sincronizarUsuarioLogado(usuarioLogadoData);
    }

    if (isSyncedEstabelecimentos && estabelecimentosData) {
      sincronizarEstabelecimentos(estabelecimentosData);
    }

    if (isSyncedEnsaios && ensaiosData) {
      sincronizarEnsaios(ensaiosData);
    }

    if (isSyncedServidores && servidoresData) {
      sincronizarServidores(servidoresData);
    }

    if (isSyncedProdutosAutorizados && produtosAutorizadosData) {
      sincronizarProdutosAutorizados(produtosAutorizadosData);
    }

    if (isSyncedMarcasProdutos && marcasProdutosData) {
      sincronizarMarcasProdutos(marcasProdutosData);
    }

    if (isSyncedProgramacoes && programacoesData) {
      sincronizarProgramacoes(programacoesData);
    }

    if (isSyncedMotivosNotificacao && motivosNotificacaoData) {
      sincronizarMotivosNotificacao(motivosNotificacaoData);
    }

    if (isSyncedFormularios && formulariosData) {
      sincronizarFormularios(formulariosData);
    }

    if (isSyncedLaboratorios && laboratoriosData) {
      sincronizarLaboratorios(laboratoriosData);
    }

    // eslint-disable-next-line
  }, [
    isSyncedUsuarioLogado,
    usuarioLogadoData,
    isSyncedEstabelecimentos,
    estabelecimentosData,
    isSyncedEnsaios,
    ensaiosData,
    isSyncedServidores,
    servidoresData,
    isSyncedProdutosAutorizados,
    produtosAutorizadosData,
    isSyncedMarcasProdutos,
    marcasProdutosData,
    isSyncedProgramacoes,
    programacoesData,
    isSyncedMotivosNotificacao,
    motivosNotificacaoData,
    isSyncedFormularios,
    formulariosData,
    isSyncedLaboratorios,
    laboratoriosData,
  ]);

  return {};
};
